import React, {useState} from "react";
import {useFormContext} from "react-hook-form";
import {DndContext, DragEndEvent, closestCenter} from "@dnd-kit/core";
import {restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {SortableContext, arrayMove, useSortable, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {IconButton, Menu, Radio} from "@mui/material";
import {Box, Colors, IconClose, Text} from "@bounce/shared/ui";
import {PaymentMethod} from "../../../../apis/requests/payment-flows/types";
import {useSortableSensors} from "../../../../components/sortable";
import {Button, MenuItem, Tooltip} from "../../../../ui/components";
import {PAYMENT_METHOD_OPTIONS, PAYMENT_METHODS} from "../constants";
import {FormValues} from "./schema";

const DRAG_ACTIVATION_DISTANCE = 4;
const ROW_HEIGHT = 44;
const POSITION_SIZE = 20;
const METHOD_ICON_SIZE = 18;
const ADD_METHOD_LABEL = "+ Add method";
const ALL_METHODS_ADDED_LABEL = "All methods added";
const DEFAULT_LABEL = "Default";
const LAST_METHOD_TOOLTIP = "A variant must show at least one method";

type MethodRowProps = {
  method: PaymentMethod;
  position: number;
  isDefault: boolean;
  canRemove: boolean;
  onSetDefault: () => void;
  onRemove: () => void;
};

const MethodRow = ({method, position, isDefault, canRemove, onSetDefault, onRemove}: MethodRowProps) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: method});
  const {label, Icon} = PAYMENT_METHOD_OPTIONS[method];

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        height: ROW_HEIGHT,
        px: 1,
        border: `1px solid ${isDefault ? Colors.blue._400 : Colors.tertiary._300}`,
        borderRadius: "8px",
        backgroundColor: isDefault ? Colors.blue._100 : Colors.bw.white,
        opacity: isDragging ? 0.5 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${label}`}
        sx={{display: "flex", cursor: "grab", color: Colors.grey._400, "&:active": {cursor: "grabbing"}}}
      >
        <DragIndicatorIcon sx={{fontSize: 18}} />
      </Box>
      <Box
        sx={{
          width: POSITION_SIZE,
          height: POSITION_SIZE,
          borderRadius: "4px",
          backgroundColor: Colors.tertiary._200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text size={11} variant="inter600" sx={{color: Colors.grey._700}}>
          {position}
        </Text>
      </Box>
      <Icon width={METHOD_ICON_SIZE} height={METHOD_ICON_SIZE} color={Colors.grey._900} />
      <Text size={14} variant="inter600" sx={{color: Colors.grey._900}}>
        {label}
      </Text>
      <Text size={12} sx={{color: Colors.grey._500, fontFamily: "monospace"}}>
        {method}
      </Text>
      <Box sx={{flex: 1}} />
      <Box
        component="label"
        sx={{display: "flex", alignItems: "center", cursor: "pointer", color: Colors.grey._700, fontSize: 13}}
      >
        <Radio size="small" checked={isDefault} onChange={onSetDefault} value={method} />
        {DEFAULT_LABEL}
      </Box>
      <Tooltip title={canRemove ? "" : LAST_METHOD_TOOLTIP}>
        <span>
          <IconButton size="small" aria-label={`Remove ${label}`} onClick={onRemove} disabled={!canRemove}>
            <IconClose width={16} height={16} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

const MethodList = ({variantIndex}: {variantIndex: number}) => {
  const {watch, setValue} = useFormContext<FormValues>();
  const policyPath = `variants.${variantIndex}.policy` as const;
  const methods = watch(`${policyPath}.methods`);
  const defaultMethod = watch(`${policyPath}.default`);
  const sensors = useSortableSensors(DRAG_ACTIVATION_DISTANCE);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const missingMethods = PAYMENT_METHODS.filter((method) => !methods.includes(method));

  const setMethods = (next: PaymentMethod[]) => setValue(`${policyPath}.methods`, next, {shouldDirty: true});
  const setDefault = (method: PaymentMethod) => setValue(`${policyPath}.default`, method, {shouldDirty: true});

  const handleDragEnd = ({active, over}: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = methods.indexOf(active.id as PaymentMethod);
    const to = methods.indexOf(over.id as PaymentMethod);
    if (from === -1 || to === -1) return;
    setMethods(arrayMove(methods, from, to));
  };

  const removeMethod = (method: PaymentMethod) => {
    const remaining = methods.filter((candidate) => candidate !== method);
    setMethods(remaining);
    if (method === defaultMethod && remaining.length > 0) setDefault(remaining[0]);
  };

  const addMethod = (method: PaymentMethod) => {
    setAddAnchor(null);
    setMethods([...methods, method]);
  };

  return (
    <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={methods} strategy={verticalListSortingStrategy}>
          {methods.map((method, index) => (
            <MethodRow
              key={method}
              method={method}
              position={index + 1}
              isDefault={method === defaultMethod}
              canRemove={methods.length > 1}
              onSetDefault={() => setDefault(method)}
              onRemove={() => removeMethod(method)}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Box>
        <Button
          buttonType="text"
          size="small"
          disabled={missingMethods.length === 0}
          onClick={(event: React.MouseEvent<HTMLElement>) => setAddAnchor(event.currentTarget)}
        >
          {missingMethods.length === 0 ? ALL_METHODS_ADDED_LABEL : ADD_METHOD_LABEL}
        </Button>
        <Menu anchorEl={addAnchor} open={!!addAnchor} onClose={() => setAddAnchor(null)}>
          {missingMethods.map((method) => (
            <MenuItem key={method} onClick={() => addMethod(method)}>
              {PAYMENT_METHOD_OPTIONS[method].label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  );
};

export default MethodList;
